/**
 * SchedulePost — Pro/Free dashboard for the Sunday Schedule Post Generator.
 *
 * Free tier: pick 1 of 3 generic templates, toggle Sunday emails on/off,
 * preview the upcoming-week image, send the email now, generate a one-off
 * Quick Post for any pop-up / "tomorrow we're at X" announcement.
 *
 * Pro tier (gated by user.is_pro): everything above + truck's logo is
 * automatically overlaid + brand primary/accent color overrides + a
 * curated 8-palette preset picker that updates both colors at once.
 *
 * The preview image is a live PNG fetched from
 * `/api/schedule-post/preview/{truck_id}` — re-fetched whenever the
 * user changes template, format, or colors. The same renderer is
 * used for the Sunday email send so what you see is what you ship.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { BACKEND_BASE } from "../lib/backendBase";
import api, { getAuthToken } from "../lib/api";
import { AuthImage } from "../components/AuthImage";
import { fetchAndShare, sharePost } from "../lib/sharePost";
import { toast } from "sonner";

const TEMPLATES = ["classic", "bold", "festival"];
const PRO_TEMPLATES = ["photo_cards"];
const TEMPLATE_LABELS = { photo_cards: "Photo Cards" };
const FORMATS = [
  { key: "square", label: "Instagram Feed (1:1)" },
  { key: "story", label: "Story (9:16)" },
];

// 8 brand-palette presets — taps the brand-color picker to one click for
// the most common food-truck aesthetics (warm/cool/playful/professional).
const PALETTE_PRESETS = [
  { name: "Ocean", primary: "#0EA5E9", accent: "#0369A1" },
  { name: "Sunset", primary: "#FB923C", accent: "#DC2626" },
  { name: "Mint", primary: "#10B981", accent: "#047857" },
  { name: "Berry", primary: "#E11D48", accent: "#9F1239" },
  { name: "Royal", primary: "#7C3AED", accent: "#5B21B6" },
  { name: "Sand", primary: "#D97706", accent: "#92400E" },
  { name: "Midnight", primary: "#1E293B", accent: "#FB923C" },
  { name: "Coral", primary: "#F87171", accent: "#BE123C" },
];

export default function SchedulePost() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [truckIdx, setTruckIdx] = useState(0);
  const [format, setFormat] = useState("square");
  const [week, setWeek] = useState("next");
  const [day, setDay] = useState("all");
  const [previewKey, setPreviewKey] = useState(0); // forces <img> re-fetch
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({
    title: "Tomorrow!",
    location: "",
    time_label: "5pm – 9pm",
    date_label: "",
    template: "classic",
    fmt: "square",
  });

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
        const r = await api.get("/schedule-post/settings");
        setTrucks(r.data.trucks || []);
      } catch (e) {
        toast.error("Couldn't load your schedule post settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const truck = trucks[truckIdx];
  const sp = truck?.schedule_post || { template: "classic", enabled: true };
  const isPro = !!user?.is_pro;

  // Reload preview when any visible variable changes.
  // `use_custom=false` ensures the 3 built-in templates render here even
  // when a custom template is uploaded via Pro Lab — Pro Lab has its
  // own page for editing on top of the custom template.
  const weekQs = `&week=${week}${day === "all" ? "" : `&day=${day}`}`;

  const previewUrl = useMemo(() => {
    if (!truck) return null;
    const base = BACKEND_BASE || "";
    const t = sp.template || "classic";
    // photo_cards consumes the uploaded photo as its background, so it
    // needs use_custom=true; the 3 built-ins keep rendering clean here.
    const useCustom = t === "photo_cards" ? "true" : "false";
    // Cache-buster forces the <img> to re-fetch after settings save.
    return `${base}/api/schedule-post/preview/${truck.truck_id}?template=${t}&fmt=${format}&use_custom=${useCustom}${weekQs}&v=${previewKey}`;
  }, [truck, sp.template, format, previewKey, weekQs]);

  // Layered preview (photo_cards): transparent overlay + raw photo
  // underneath so the photo pans live without moving cards/logo/footer.
  const overlayUrl = useMemo(
    () => (previewUrl && sp.template === "photo_cards" ? `${previewUrl}&layer=overlay` : null),
    [previewUrl, sp.template],
  );
  const rawPhotoUrl = useMemo(() => {
    if (!truck || !sp.custom_template_path) return null;
    const base = BACKEND_BASE || "";
    return `${base}/api/schedule-post/lab/template-image/${truck.truck_id}?path=${encodeURIComponent(sp.custom_template_path)}`;
  }, [truck, sp.custom_template_path]);


  // Background photo upload — right here on the Sunday page (June 2026).
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !truck) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Upload a PNG or JPG photo");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo too large — max 8 MB");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/schedule-post/lab/template/${truck.truck_id}`, fd);
      setTrucks((prev) => prev.map((t, i) => {
        if (i !== truckIdx) return t;
        const spc = { ...(t.schedule_post || {}) };
        const lib = (spc.photo_library && spc.photo_library.length
          ? spc.photo_library
          : spc.custom_template_path ? [{ path: spc.custom_template_path }] : []
        ).concat([{ path: data.path }]).slice(-8);
        return { ...t, schedule_post: { ...spc, photo_library: lib, custom_template_path: data.path } };
      }));
      setPreviewKey((k) => k + 1);
      toast.success("Background photo updated");
    } catch (err) {
      toast.error("Upload failed — try a PNG or JPG under 8 MB");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const photoLib = useMemo(() => {
    const lib = sp.photo_library || [];
    if (!lib.length && sp.custom_template_path) return [{ path: sp.custom_template_path }];
    return lib;
  }, [sp.photo_library, sp.custom_template_path]);

  const activatePhoto = async (path) => {
    if (!truck || path === sp.custom_template_path) return;
    try {
      await api.post(`/schedule-post/photos/${truck.truck_id}/activate`, { path });
      setTrucks((prev) => prev.map((t, i) =>
        i === truckIdx
          ? { ...t, schedule_post: { ...(t.schedule_post || {}), custom_template_path: path } }
          : t));
      setPreviewKey((k) => k + 1);
      toast.success("Background switched");
    } catch {
      toast.error("Couldn't switch photo");
    }
  };

  const deletePhoto = async (path) => {
    if (!truck) return;
    if (!window.confirm("Delete this photo from your library?")) return;
    try {
      await api.delete(`/schedule-post/lab/template/${truck.truck_id}?path=${encodeURIComponent(path)}`);
      setTrucks((prev) => prev.map((t, i) => {
        if (i !== truckIdx) return t;
        const spc = { ...(t.schedule_post || {}) };
        const lib = (spc.photo_library && spc.photo_library.length
          ? spc.photo_library
          : spc.custom_template_path ? [{ path: spc.custom_template_path }] : []
        ).filter((p) => p.path !== path);
        spc.photo_library = lib;
        if (spc.custom_template_path === path) {
          if (lib.length) spc.custom_template_path = lib[lib.length - 1].path;
          else delete spc.custom_template_path;
        }
        return { ...t, schedule_post: spc };
      }));
      setPreviewKey((k) => k + 1);
      toast.success("Photo deleted");
    } catch {
      toast.error("Couldn't delete photo");
    }
  };

  const updateSettings = async (patch) => {
    if (!truck) return false;
    try {
      await api.post(`/schedule-post/settings/${truck.truck_id}`, patch);
      setTrucks((prev) => prev.map((t, i) =>
        i === truckIdx ? { ...t, schedule_post: { ...sp, ...patch } } : t,
      ));
      setPreviewKey((k) => k + 1);
      toast.success("Saved");
      return true;
    } catch (e) {
      toast.error("Couldn't save — try again");
      return false;
    }
  };

  // Drag-to-reposition the photo inside the frame (photo_cards only).
  // The preview is layered: raw photo underneath (pans instantly via
  // CSS object-position) + transparent overlay (cards/logo/footer) on
  // top that never moves. Save fires only when the drag ends.
  const dragRef = useRef(null);
  const [livePos, setLivePos] = useState(null);
  const [liveZoom, setLiveZoom] = useState(null);
  const [liveOpacity, setLiveOpacity] = useState(null);
  const [dragSaving, setDragSaving] = useState(false);
  const pcDraggable = (sp.template === "photo_cards") && photoLib.length > 0;
  const hasManual = typeof sp.pc_offset_x === "number" && sp.pc_offset_x >= 0;
  const zoom = liveZoom ?? (typeof sp.pc_zoom === "number" && sp.pc_zoom > 100 ? sp.pc_zoom : 100);
  const photoOpacity = liveOpacity ?? (typeof sp.pc_photo_opacity === "number" && sp.pc_photo_opacity < 100 ? sp.pc_photo_opacity : 100);
  const pos = livePos || (hasManual
    ? {
        x: sp.pc_offset_x,
        y: typeof sp.pc_offset_y === "number" && sp.pc_offset_y >= 0 ? sp.pc_offset_y : 50,
      }
    : null);
  const layered = pcDraggable && !!pos && !!rawPhotoUrl && !!overlayUrl;
  const clamp01 = (v) => Math.min(100, Math.max(0, v));
  const onPreviewPointerDown = (e) => {
    if (!pcDraggable || dragSaving) return;
    const start = { x: pos ? pos.x : 50, y: pos ? pos.y : 50 };
    dragRef.current = { x: e.clientX, y: e.clientY, ox: start.x, oy: start.y };
    if (!pos) setLivePos(start);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPreviewPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !pcDraggable) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // Dragging the image right slides the crop window left (and vice
    // versa); 120 = sensitivity so a full-width drag spans the range.
    setLivePos({
      x: clamp01(d.ox - ((e.clientX - d.x) / rect.width) * 120),
      y: clamp01(d.oy - ((e.clientY - d.y) / rect.height) * 120),
    });
  };
  const onPreviewPointerCancel = () => { dragRef.current = null; };
  const clearDrag = () => { setLivePos(null); setLiveZoom(null); setLiveOpacity(null); setDragSaving(false); };
  const onPreviewPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !pcDraggable) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) < 6 && Math.abs(dy) < 6) {
      if (!hasManual) setLivePos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = Math.round(clamp01(d.ox - (dx / rect.width) * 120));
    const ny = Math.round(clamp01(d.oy - (dy / rect.height) * 120));
    setLivePos({ x: nx, y: ny });
    setDragSaving(true);
    updateSettings({ pc_offset_x: nx, pc_offset_y: ny })
      .then((ok) => { if (!ok) clearDrag(); });
  };
  const commitZoom = () => {
    const z = liveZoom;
    if (z == null || z === (sp.pc_zoom || 100)) return;
    const patch = { pc_zoom: z };
    if (!hasManual) {
      patch.pc_offset_x = 50;
      patch.pc_offset_y = 50;
      setLivePos({ x: 50, y: 50 });
    }
    setDragSaving(true);
    updateSettings(patch).then((ok) => { if (!ok) clearDrag(); });
  };
  const commitOpacity = () => {
    const o = liveOpacity;
    if (o == null || o === (sp.pc_photo_opacity || 100)) return;
    setDragSaving(true);
    updateSettings({ pc_photo_opacity: o }).then((ok) => { if (!ok) clearDrag(); });
  };

  const sendNow = async () => {
    if (!truck) return;
    try {
      const r = await api.post(`/schedule-post/send-now/${truck.truck_id}`);
      if (r.data?.ok) toast.success("Email sent to your inbox");
      else toast.warning(r.data?.reason === "no_events_next_week"
        ? "No schedule events for next week — nothing to send"
        : "Send failed — try again");
    } catch {
      toast.error("Send failed");
    }
  };

  const downloadWeekly = async () => {
    if (!truck) return;
    try {
      const base = BACKEND_BASE || "";
      const res = await fetch(
        `${base}/api/schedule-post/download/${truck.truck_id}?fmt=${format}${weekQs}`, {
          credentials: "include",
          cache: "no-store",
          headers: { Authorization: `Bearer ${getAuthToken() || ''}` },
        });
      if (!res.ok) throw new Error("bad response");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `${truck.truck_name || 'truck'}-weekly.png`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  // One-tap share to Instagram / Photos / Messages via the OS share
  // sheet. On iOS Capacitor this hits the native share plugin; on
  // mobile browsers it uses the Web Share API; on desktop it falls
  // back to a download (since most desktop browsers don't support
  // sharing files via the Web Share API).
  const shareWeekly = () => {
    if (!truck) return;
    const base = BACKEND_BASE || "";
    fetchAndShare({
      url: `${base}/api/schedule-post/download/${truck.truck_id}?fmt=${format}${weekQs}`,
      token: getAuthToken() || "",
      filename: `${truck.truck_name || "truck"}-weekly.png`,
      title: `${truck.truck_name || "Our Truck"} — this week's schedule`,
      truckId: truck.truck_id,
    });
  };

  const shareQuickPost = async () => {
    if (!truck) return;
    try {
      const base = BACKEND_BASE || "";
      const res = await fetch(`${base}/api/schedule-post/quick-post/${truck.truck_id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken() || ''}` },
        body: JSON.stringify(quick),
      });
      if (!res.ok) throw new Error("bad response");
      const blob = await res.blob();
      await sharePost({
        blob,
        filename: `${truck.truck_name || 'truck'}-quickpost.png`,
        title: `${truck.truck_name || 'Our Truck'} — ${quick.title || 'Pop-up'}`,
        text: `${quick.title || ''} ${quick.location ? `· ${quick.location}` : ''}`.trim(),
        truckId: truck.truck_id,
      });
    } catch {
      toast.error("Share failed");
    }
  };

  const downloadQuickPost = async () => {
    if (!truck) return;
    try {
      const base = BACKEND_BASE || "";
      const res = await fetch(`${base}/api/schedule-post/quick-post/${truck.truck_id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken() || ''}` },
        body: JSON.stringify(quick),
      });
      if (!res.ok) throw new Error("bad response");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${truck.truck_name || 'truck'}-quickpost.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch {
      toast.error("Quick post generation failed");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><div className="p-12 text-center text-muted-foreground">Loading…</div></div>
  );
  if (!trucks.length) return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto p-12 text-center">
        <h1 className="text-2xl font-bold mb-2">No trucks yet</h1>
        <p className="text-muted-foreground">Create a truck profile first, then come back here to set up your weekly schedule post.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="schedule-post-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Post Studio</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Create schedule images for social — pick a week or a single day. Your weekly post is still auto-emailed every Sunday at 7am.
          </p>
        </header>

        {trucks.length > 1 && (
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Truck</label>
            <select value={truckIdx} onChange={(e) => { setTruckIdx(+e.target.value); setPreviewKey(k=>k+1); }}
                    className="w-full sm:w-auto rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="truck-select">
              {trucks.map((t, i) => <option key={t.truck_id} value={i}>{t.truck_name}</option>)}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
          {/* Preview pane */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold">Preview</h2>
              <div className="flex rounded-md border overflow-hidden">
                {["this", "next"].map(w => (
                  <button key={w} onClick={() => setWeek(w)}
                          data-testid={`week-${w}`}
                          className={`text-xs px-3 py-1.5 transition-colors ${
                            week === w ? "bg-foreground text-background" : "hover:bg-muted"}`}>
                    {w === "this" ? "This week" : "Next week"}
                  </button>
                ))}
              </div>
              <select value={day} onChange={(e) => setDay(e.target.value)}
                      data-testid="day-select"
                      className="text-xs rounded-md border border-input bg-background px-2 py-1.5">
                <option value="all">Full week</option>
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                  <option key={d} value={i}>{d} only</option>
                ))}
              </select>
            </div>
            <div
              className={`relative aspect-square bg-muted rounded-xl overflow-hidden shadow-sm border select-none ${pcDraggable ? "cursor-move" : ""}`}
              style={pcDraggable ? { touchAction: "none" } : undefined}
              onPointerDown={onPreviewPointerDown}
              onPointerMove={onPreviewPointerMove}
              onPointerUp={onPreviewPointerUp}
              onPointerCancel={onPreviewPointerCancel}
              data-testid="preview-drag-area"
            >
              {previewUrl && (layered ? (
                <div
                  className="relative h-full mx-auto"
                  style={{ aspectRatio: format === "story" ? "9 / 16" : "1 / 1" }}
                  data-testid="layered-preview"
                >
                  <div
                    className="absolute inset-x-0 top-0 overflow-hidden bg-white"
                    style={{ height: `${sp.show_branding === false ? 100 : (format === "story" ? 91.5 : 86.5)}%` }}
                  >
                    <AuthImage src={rawPhotoUrl} alt="Truck photo" keepPrevious
                               className="w-full h-full object-cover pointer-events-none"
                               imgProps={{
                                 style: {
                                   objectPosition: `${pos.x}% ${pos.y}%`,
                                   transform: `scale(${zoom / 100})`,
                                   transformOrigin: `${pos.x}% ${pos.y}%`,
                                   opacity: photoOpacity / 100,
                                 },
                                 draggable: false,
                               }}
                               testId="photo-layer" />
                  </div>
                  <AuthImage src={overlayUrl} alt="Schedule overlay" keepPrevious onReady={clearDrag}
                             className="absolute inset-0 w-full h-full pointer-events-none"
                             testId="overlay-layer" />
                </div>
              ) : (
                <AuthImage src={previewUrl} alt="Schedule preview" keepPrevious
                           className="w-full h-full object-contain pointer-events-none" testId="schedule-preview-image" />
              ))}
              {dragSaving && (
                <span
                  className="absolute top-2 right-2 text-[11px] font-medium px-2 py-1 rounded-md bg-foreground/80 text-background pointer-events-none"
                  data-testid="preview-updating-badge"
                >
                  Updating…
                </span>
              )}
            </div>
            {pcDraggable && (
              <div className="flex items-center gap-3 -mt-3 text-xs text-muted-foreground select-none">
                <span>Zoom</span>
                <input type="range" min="100" max="300" step="5" value={zoom}
                       onChange={(e) => setLiveZoom(Number(e.target.value))}
                       onPointerUp={commitZoom}
                       onKeyUp={commitZoom}
                       data-testid="photo-zoom-slider"
                       className="w-40 accent-foreground cursor-pointer" />
                <span data-testid="photo-zoom-value" className="tabular-nums w-10">{Math.round(zoom)}%</span>
              </div>
            )}
            {pcDraggable && (
              <div className="flex items-center gap-3 -mt-3 text-xs text-muted-foreground select-none">
                <span>Photo fade</span>
                <input type="range" min="20" max="100" step="5" value={photoOpacity}
                       onChange={(e) => setLiveOpacity(Number(e.target.value))}
                       onPointerUp={commitOpacity}
                       onKeyUp={commitOpacity}
                       data-testid="photo-opacity-slider"
                       className="w-40 accent-foreground cursor-pointer" />
                <span data-testid="photo-opacity-value" className="tabular-nums w-10">{Math.round(photoOpacity)}%</span>
              </div>
            )}
            {pcDraggable && (
              <p className="text-xs text-muted-foreground -mt-3 flex items-center gap-2">
                <span>Drag the preview to reposition your photo.</span>
                {(typeof sp.pc_offset_x === "number" && sp.pc_offset_x >= 0) || (sp.pc_zoom || 100) > 100 || (sp.pc_photo_opacity || 100) < 100 ? (
                  <button
                    onClick={() => { clearDrag(); updateSettings({ pc_offset_x: -1, pc_offset_y: -1, pc_zoom: 100, pc_photo_opacity: 100 }); }}
                    data-testid="reset-photo-position-btn"
                    className="underline hover:no-underline"
                  >
                    Reset position
                  </button>
                ) : null}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {FORMATS.map(f => (
                <button key={f.key} onClick={() => setFormat(f.key)}
                        data-testid={`format-${f.key}`}
                        className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${
                          format === f.key ? "bg-foreground text-background" : "hover:bg-muted"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Controls pane */}
          <div className="space-y-6">
            <input ref={photoInputRef} type="file" accept="image/png,image/jpeg"
                   onChange={onPhotoPicked} className="hidden"
                   data-testid="background-photo-input" />
            {/* Photo library — multiple uploads, tap to activate. */}
            <div data-testid="background-photo-section"
                 className="rounded-lg border p-3 text-sm">
              <div className="font-medium mb-1">Photos</div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Upload up to 8 truck photos — tap one to make it your Photo Cards background.
              </p>
              {photoLib.length > 0 && truck && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {photoLib.map((p) => {
                    const active = p.path === sp.custom_template_path;
                    return (
                      <div key={p.path} className="relative group">
                        <button onClick={() => activatePhoto(p.path)}
                                data-testid="photo-thumb"
                                aria-label={active ? "Active photo" : "Use this photo"}
                                className={`block h-16 w-16 rounded-md overflow-hidden border transition-opacity ${
                                  active ? "ring-2 ring-foreground" : "opacity-75 hover:opacity-100"}`}>
                          <AuthImage
                            src={`${BACKEND_BASE}/api/schedule-post/lab/template-image/${truck.truck_id}?path=${encodeURIComponent(p.path)}`}
                            alt="Truck photo" silent
                            className="h-16 w-16 object-cover" />
                        </button>
                        <button onClick={() => deletePhoto(p.path)}
                                data-testid="photo-delete"
                                aria-label="Delete photo"
                                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-foreground text-background text-[10px] leading-none hidden group-hover:flex items-center justify-center">
                          ✕
                        </button>
                        {active && (
                          <span className="absolute bottom-0 inset-x-0 bg-foreground/80 text-background text-[9px] text-center rounded-b-md pointer-events-none">
                            Active
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-2">
                <button
                  onClick={() => {
                    if (!isPro) {
                      toast("Custom background photos are a Pro perk", {
                        description: "Upgrade to Pro to use your own photos.",
                      });
                      return;
                    }
                    photoInputRef.current?.click();
                  }}
                  disabled={uploadingPhoto || photoLib.length >= 8}
                  data-testid="change-background-photo-btn"
                  className="text-xs px-3 py-1.5 rounded-md bg-foreground text-background font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {uploadingPhoto ? "Uploading…"
                    : photoLib.length >= 8 ? "Library full (8)"
                    : photoLib.length ? "Add photo" : "Upload photo"}
                </button>
              </div>
            </div>
            {/* Template picker */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Template</h2>
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map(t => (
                  <button key={t} onClick={() => updateSettings({ template: t })}
                          data-testid={`template-${t}`}
                          className={`text-sm px-3 py-3 rounded-md border capitalize transition-all ${
                            sp.template === t ? "ring-2 ring-foreground bg-muted" : "hover:bg-muted"}`}>
                    {t}
                  </button>
                ))}
              </div>
              {/* Pro-only "Photo Cards" — photo background + white event
                  cards + branded footer. Needs a linked calendar (Pro). */}
              <div className="grid grid-cols-1 gap-2 mt-2">
                {PRO_TEMPLATES.map(t => (
                  <button key={t}
                          onClick={() => {
                            if (!isPro) {
                              toast("Photo Cards is a Pro template", {
                                description: "Upgrade to Pro to turn your truck photo into a weekly schedule post.",
                              });
                              return;
                            }
                            updateSettings({ template: t });
                          }}
                          data-testid={`template-${t}`}
                          className={`text-sm px-3 py-3 rounded-md border transition-all flex items-center justify-center gap-2 ${
                            sp.template === t ? "ring-2 ring-foreground bg-muted" : "hover:bg-muted"} ${
                            !isPro ? "opacity-60" : ""}`}>
                    {TEMPLATE_LABELS[t] || t}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">PRO</span>
                  </button>
                ))}
              </div>
              {sp.template === "photo_cards" && (
                <>
                  <p className="text-xs text-muted-foreground mt-2" data-testid="photo-cards-hint">
                    Uses your uploaded photo (above) as the background — your week's
                    events render as cards on top, with the TruckTrackr footer.
                    Private and staff-only events never appear on your post.
                  </p>
                  {/* Layout overrides — Auto = smart photo analysis. */}
                  <div className="flex gap-2 mt-2">
                    <select
                      value={sp.pc_cards_side || "auto"}
                      onChange={(e) => updateSettings({ pc_cards_side: e.target.value })}
                      className="flex-1 text-sm rounded-md border bg-background px-2 py-2"
                      data-testid="pc-cards-side-select"
                    >
                      <option value="auto">Cards: Auto (smart)</option>
                      <option value="left">Cards: Left side</option>
                      <option value="right">Cards: Right side</option>
                      <option value="center">Cards: Centered</option>
                      <option value="top">Cards: Top</option>
                      <option value="bottom">Cards: Bottom</option>
                    </select>
                    <select
                      value={sp.pc_logo_corner || "auto"}
                      onChange={(e) => updateSettings({ pc_logo_corner: e.target.value })}
                      className="flex-1 text-sm rounded-md border bg-background px-2 py-2"
                      data-testid="pc-logo-corner-select"
                    >
                      <option value="auto">Logo: Auto corner</option>
                      <option value="top-left">Logo: Top left</option>
                      <option value="top-right">Logo: Top right</option>
                      <option value="bottom-left">Logo: Bottom left</option>
                      <option value="bottom-right">Logo: Bottom right</option>
                    </select>
                  </div>
                  {/* Headline + logo visibility toggles (Pro photo_cards). */}
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={sp.pc_show_logo !== false}
                        onChange={(e) => updateSettings({ pc_show_logo: e.target.checked })}
                        data-testid="pc-show-logo-checkbox"
                        className="h-4 w-4 rounded border"
                      />
                      <span>Show logo</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={sp.pc_show_headline !== false}
                        onChange={(e) => updateSettings({ pc_show_headline: e.target.checked })}
                        data-testid="pc-show-headline-checkbox"
                        className="h-4 w-4 rounded border"
                      />
                      <span>Show "THIS WEEK"</span>
                    </label>
                  </div>
                  {/* Custom headline — replaces "THIS WEEK" (blank = default). */}
                  <input
                    key={`hl-${truckIdx}`}
                    type="text"
                    maxLength={32}
                    placeholder="Headline text (default: THIS WEEK)"
                    defaultValue={sp.pc_headline || ""}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (sp.pc_headline || "")) updateSettings({ pc_headline: v });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    data-testid="pc-headline-input"
                    className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                  {/* Card/headline font (Pro) — the piece of Pro Lab
                      worth keeping after the lab was retired. */}
                  <div className="mt-2">
                    <select value={sp.pc_font || "default"}
                            onChange={(e) => updateSettings({ pc_font: e.target.value })}
                            data-testid="pc-font-select"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      <option value="default">Font: Default</option>
                      <option value="oswald">Font: Oswald (bold)</option>
                      <option value="bebas">Font: Bebas Neue</option>
                      <option value="lobster">Font: Lobster</option>
                      <option value="pacifico">Font: Pacifico</option>
                      <option value="marker">Font: Permanent Marker</option>
                      <option value="playfair">Font: Playfair Display</option>
                    </select>
                  </div>
                </>
              )}
              {/* "Powered by TruckTrackr" footer toggle — Pro can remove it. */}
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sp.show_branding !== false}
                  onChange={(e) => {
                    if (!isPro) {
                      toast("Removing the footer is a Pro perk", {
                        description: "Upgrade to Pro to publish schedule images without TruckTrackr branding.",
                      });
                      return;
                    }
                    updateSettings({ show_branding: e.target.checked });
                  }}
                  data-testid="show-branding-checkbox"
                  className="h-4 w-4 rounded border"
                />
                <span>
                  "Powered by TruckTrackr" footer
                  {!isPro && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                      PRO to remove
                    </span>
                  )}
                </span>
              </label>
            </div>

            {/* Brand colors (Pro) */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold">Brand Colors</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${isPro ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground"}`}>
                  {isPro ? "Pro" : "Pro only"}
                </span>
              </div>
              <div className={isPro ? "" : "opacity-40 pointer-events-none"}>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {PALETTE_PRESETS.map(p => (
                    <button key={p.name} title={p.name}
                            onClick={() => updateSettings({ primary_color: p.primary, accent_color: p.accent })}
                            data-testid={`palette-${p.name.toLowerCase()}`}
                            className="aspect-square rounded-md border hover:ring-2 hover:ring-foreground/40 transition-all"
                            style={{ background: `linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)` }} />
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs"><span className="block mb-1">Primary</span>
                    <input type="color" value={sp.primary_color || "#0EA5E9"}
                           onChange={(e) => updateSettings({ primary_color: e.target.value })}
                           data-testid="primary-color-picker"
                           className="w-full h-10 rounded border cursor-pointer" />
                  </label>
                  <label className="text-xs"><span className="block mb-1">Accent</span>
                    <input type="color" value={sp.accent_color || "#FB923C"}
                           onChange={(e) => updateSettings({ accent_color: e.target.value })}
                           data-testid="accent-color-picker"
                           className="w-full h-10 rounded border cursor-pointer" />
                  </label>
                </div>
                {!isPro && (
                  <p className="text-xs text-muted-foreground mt-3">Upgrade to Pro to use your truck&apos;s logo + custom brand colors in every post.</p>
                )}
              </div>
            </div>

            {/* Sunday email toggle + send-now */}
            <div className="space-y-3 border-t pt-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={sp.enabled !== false}
                       onChange={(e) => updateSettings({ enabled: e.target.checked })}
                       data-testid="sunday-email-toggle"
                       className="w-5 h-5 rounded" />
                <span className="text-sm">Email me my weekly schedule post every Sunday at 7am</span>
              </label>
              <button onClick={sendNow} data-testid="send-now-button"
                      className="w-full px-4 py-2.5 rounded-md bg-foreground text-background font-medium hover:opacity-90 transition-opacity">
                Send to my email now
              </button>
              <button onClick={downloadWeekly} data-testid="download-weekly-button"
                      className="w-full px-4 py-2.5 rounded-md bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors">
                ⬇️ Download this week&apos;s post
              </button>
              <button onClick={shareWeekly} data-testid="share-weekly-button"
                      className="w-full px-4 py-2.5 rounded-md border-2 border-foreground font-medium hover:bg-muted transition-colors">
                📤 Share
              </button>
              <button onClick={() => setQuickOpen(true)} data-testid="quick-post-open-button"
                      className="w-full px-4 py-2.5 rounded-md border-2 border-dashed font-medium hover:bg-muted transition-colors">
                ⚡ Generate a Quick Post
              </button>
            </div>
          </div>
        </div>

        {/* Quick Post modal */}
        {quickOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setQuickOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-background rounded-xl shadow-2xl w-full max-w-md p-6"
                 data-testid="quick-post-modal">
              <h2 className="text-xl font-bold mb-1">Quick Post</h2>
              <p className="text-sm text-muted-foreground mb-4">For pop-ups, &ldquo;tomorrow!&rdquo; announcements, special events.</p>
              <div className="space-y-3">
                {[
                  ["title", "Title (e.g. TOMORROW!)", "text"],
                  ["location", "Location (e.g. Beer Garden)", "text"],
                  ["date_label", "Date label (e.g. Friday, June 27)", "text"],
                  ["time_label", "Time label (e.g. 5pm – 9pm)", "text"],
                ].map(([key, label, type]) => (
                  <label key={key} className="block text-xs">
                    <span className="block mb-1 font-medium">{label}</span>
                    <input type={type} value={quick[key]}
                           onChange={(e) => setQuick({...quick, [key]: e.target.value})}
                           data-testid={`quick-${key}`}
                           className="w-full rounded-md border px-3 py-2 text-sm" />
                  </label>
                ))}
                <div className="grid grid-cols-2 gap-2">
                  <select value={quick.template} onChange={(e) => setQuick({...quick, template: e.target.value})}
                          data-testid="quick-template"
                          className="rounded-md border px-2 py-2 text-sm">
                    {TEMPLATES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={quick.fmt} onChange={(e) => setQuick({...quick, fmt: e.target.value})}
                          data-testid="quick-format"
                          className="rounded-md border px-2 py-2 text-sm">
                    <option value="square">Square 1:1</option>
                    <option value="story">Story 9:16</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setQuickOpen(false)} className="flex-1 px-4 py-2 rounded-md border">Cancel</button>
                <button onClick={downloadQuickPost} data-testid="quick-post-download"
                        className="flex-1 px-4 py-2 rounded-md bg-amber-500 text-white font-medium">
                  ⬇️ Download
                </button>
                <button onClick={shareQuickPost} data-testid="quick-post-share"
                        className="flex-1 px-4 py-2 rounded-md bg-foreground text-background font-medium">
                  📤 Share
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
